import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
// Deep imports so this lazy-loadable setup screen does not, by being
// statically imported from AppComponent, evaluate the simulation-core
// barrel (which re-exports CastOffProApp from `./main`).
import { defaultAppState } from "../../simulation-core/State";
import type { AppState } from "../../simulation-core/State";
import {
  DEFAULT_SCENE,
  SCENE_PRESETS,
  applySceneToState,
  terrainCenterFromBbox,
  type SceneDefinition,
} from "../../simulation-core/sim/Scene";
import {
  CASTOFFPRO_PLAYER_BOAT_ID,
  CASTOFFPRO_TERRAIN_ID,
} from "../core/storage/storage-keys";
import {
  CatalogService,
  type CatalogChoice,
} from "../core/catalog/catalog.service";

export interface GameSetupLaunch {
  readonly initialState: Partial<AppState>;
}

interface WeatherChoice {
  readonly id: keyof typeof SCENE_PRESETS;
  readonly name: string;
  readonly detail: string;
}

const WEATHER_CHOICES: WeatherChoice[] = [
  {
    id: "calm_afternoon",
    name: "Spokojne popołudnie",
    detail: "6 m/s, wiatr z zachodu",
  },
  {
    id: "morning_glass",
    name: "Poranna flauta",
    detail: "1.5 m/s, prawie gładka woda",
  },
  {
    id: "fresh_breeze",
    name: "Świeży wiatr",
    detail: "11 m/s, pełniejsze żagle",
  },
  {
    id: "golden_hour",
    name: "Złota godzina",
    detail: "4 m/s, spokojny wieczór",
  },
];

@Component({
  selector: "po-game-setup-screen",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./game-setup-screen.component.html",
  styleUrl: "./game-setup-screen.component.css",
})
export class GameSetupScreenComponent {
  readonly closable = input(false);
  readonly launch = output<GameSetupLaunch>();
  readonly cancel = output<void>();

  protected readonly terrains = signal<CatalogChoice[]>([
    {
      id: DEFAULT_SCENE.terrainId,
      name: "Zielona Zatoka (Mazury)",
      url: "terrain/zielona_zatoka/",
    },
  ]);
  protected readonly boats = signal<CatalogChoice[]>([
    { id: DEFAULT_SCENE.playerBoatId, name: "Antila 27" },
  ]);
  protected readonly weatherChoices = WEATHER_CHOICES;
  protected readonly selectedTerrainId = signal(
    readLocalStorage(CASTOFFPRO_TERRAIN_ID) ?? DEFAULT_SCENE.terrainId,
  );
  protected readonly selectedBoatId = signal(
    readLocalStorage(CASTOFFPRO_PLAYER_BOAT_ID) ?? DEFAULT_SCENE.playerBoatId,
  );
  protected readonly selectedWeatherId =
    signal<keyof typeof SCENE_PRESETS>("calm_afternoon");
  protected readonly loadingCatalog = signal(true);
  protected readonly starting = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly canStart = computed(
    () => !this.loadingCatalog() && !this.starting(),
  );

  private readonly catalog = inject(CatalogService);

  constructor() {
    void this.loadCatalogs();
  }

  protected selectTerrain(id: string): void {
    this.selectedTerrainId.set(id);
  }

  protected updateTerrain(event: Event): void {
    this.selectTerrain((event.target as HTMLSelectElement).value);
  }

  protected selectBoat(id: string): void {
    this.selectedBoatId.set(id);
  }

  protected updateBoat(event: Event): void {
    this.selectBoat((event.target as HTMLSelectElement).value);
  }

  protected selectWeather(id: keyof typeof SCENE_PRESETS): void {
    this.selectedWeatherId.set(id);
  }

  protected updateWeather(event: Event): void {
    this.selectWeather(
      (event.target as HTMLSelectElement).value as keyof typeof SCENE_PRESETS,
    );
  }

  protected async start(): Promise<void> {
    if (!this.canStart()) return;
    this.starting.set(true);
    this.error.set(null);

    const scene = this.buildScene();
    const state = defaultAppState();
    try {
      const terrain = this.terrains().find(
        (choice) => choice.id === scene.terrainId,
      );
      const terrainCenter = terrain?.url
        ? terrainCenterFromBbox(
            (
              await fetchJson<TerrainMetaJson>(
                `${ensureSlash(terrain.url)}terrain_meta.json`,
              )
            ).bbox,
          )
        : undefined;
      applySceneToState(scene, state, { terrainCenter });
    } catch {
      applySceneToState(scene, state);
    }

    this.launch.emit({ initialState: state });
  }

  protected close(): void {
    if (!this.closable()) return;
    this.cancel.emit();
  }

  private buildScene(): SceneDefinition {
    const weather = SCENE_PRESETS[this.selectedWeatherId()];
    return {
      ...weather,
      playerBoatId: this.selectedBoatId(),
      terrainId: this.selectedTerrainId(),
    };
  }

  private async loadCatalogs(): Promise<void> {
    this.loadingCatalog.set(true);
    this.error.set(null);
    try {
      const [terrains, boats] = await this.catalog.loadAll();
      if (terrains.length > 0) {
        this.terrains.set([...terrains]);
        ensureSelected(
          this.selectedTerrainId,
          terrains,
          DEFAULT_SCENE.terrainId,
        );
      }
      if (boats.length > 0) {
        this.boats.set([...boats]);
        ensureSelected(this.selectedBoatId, boats, DEFAULT_SCENE.playerBoatId);
      }
    } catch {
      this.error.set(
        "Nie udało się wczytać katalogu. Używam domyślnej konfiguracji.",
      );
    } finally {
      this.loadingCatalog.set(false);
    }
  }
}

interface TerrainMetaJson {
  readonly bbox: {
    readonly lon_min: number;
    readonly lat_min: number;
    readonly lon_max: number;
    readonly lat_max: number;
  };
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url, { cache: "no-cache" });
  if (!response.ok) throw new Error(`fetch ${url} -> HTTP ${response.status}`);
  return (await response.json()) as T;
}

function ensureSlash(url: string): string {
  return url.endsWith("/") ? url : `${url}/`;
}

function readLocalStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function ensureSelected(
  selected: { set(value: string): void; (): string },
  choices: readonly CatalogChoice[],
  fallbackId: string,
): void {
  const current = selected();
  if (choices.some((choice) => choice.id === current)) return;
  selected.set(
    choices.some((choice) => choice.id === fallbackId)
      ? fallbackId
      : choices[0]!.id,
  );
}
